import { z } from 'zod';
import { GST_INVOICE_STATUSES, PAYMENT_METHODS } from '../enums.js';
import {
  optionalDate,
  optionalText,
  paginationQuerySchema,
  toPatchSchema,
  uuidSchema,
} from './common.js';
import { currencySchema } from './organization.js';
import {
  businessManagerFilterSchema,
  type BusinessManagerRef,
  type RecordRef,
} from './records.js';

/**
 * GST invoices.
 *
 * The line carries the *original* price P. The server derives the displayed
 * price (P x 0.82), the GST (P x 0.18) and the line total (P) from it — see
 * `gst-invoice-calc.ts` — and the client never supplies any of those.
 */

const money = z.coerce.number().min(0).max(1e15);
const quantity = z.coerce.number().min(0.01).max(1_000_000);

export const gstInvoiceLineSchema = z.object({
  description: z.string().trim().min(1, 'Required').max(4000),
  quantity: quantity.default(1),
  /** The original price P — what the customer pays per unit, GST included. */
  unitPrice: money,
});
export type GstInvoiceLineInput = z.infer<typeof gstInvoiceLineSchema>;

export const createGstInvoiceSchema = z.object({
  subject: z.string().trim().min(1, 'Required').max(300),
  accountId: uuidSchema.nullish(),
  contactId: uuidSchema.nullish(),
  invoiceDate: z.iso.date(),
  dueDate: optionalDate(),
  currency: currencySchema.optional(),
  lineItems: z.array(gstInvoiceLineSchema).max(200).optional(),
  notes: optionalText(8000),
  terms: optionalText(8000),
  businessManagerId: uuidSchema.optional(),
});
export type CreateGstInvoiceInput = z.infer<typeof createGstInvoiceSchema>;

export const updateGstInvoiceSchema = toPatchSchema(createGstInvoiceSchema);
export type UpdateGstInvoiceInput = z.infer<typeof updateGstInvoiceSchema>;

/** Raise a GST invoice from an ordinary invoice's lines. The source is never modified. */
export const createGstInvoiceFromInvoiceSchema = z.object({
  invoiceDate: z.iso.date().optional(),
  dueDate: optionalDate(),
});
export type CreateGstInvoiceFromInvoiceInput = z.infer<
  typeof createGstInvoiceFromInvoiceSchema
>;

export const voidGstInvoiceSchema = z.object({
  reason: z.string().trim().min(1, 'Say why').max(500),
});
export type VoidGstInvoiceInput = z.infer<typeof voidGstInvoiceSchema>;

/** Money received. It always goes into the invoice's own bank account. */
export const createGstPaymentSchema = z.object({
  amount: z.coerce.number().positive('Enter an amount greater than zero').max(1e15),
  receivedOn: z.iso.date(),
  method: z.enum(PAYMENT_METHODS).default('BANK_TRANSFER'),
  reference: optionalText(120),
  notes: optionalText(2000),
});
export type CreateGstPaymentInput = z.infer<typeof createGstPaymentSchema>;

export const GST_INVOICE_SORT_FIELDS = [
  'createdAt',
  'updatedAt',
  'invoiceDate',
  'dueDate',
  'total',
  'status',
] as const;

export const listGstInvoicesQuerySchema = paginationQuerySchema.extend({
  sortBy: z.enum(GST_INVOICE_SORT_FIELDS).default('createdAt'),
  businessManagerId: businessManagerFilterSchema.optional(),
  status: z.enum(GST_INVOICE_STATUSES).optional(),
  accountId: uuidSchema.optional(),
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
});
export type ListGstInvoicesQuery = z.infer<typeof listGstInvoicesQuerySchema>;

export const gstReportQuerySchema = z.object({
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
});
export type GstReportQuery = z.infer<typeof gstReportQuerySchema>;

export interface GstInvoiceLineView {
  id: string;
  description: string;
  quantity: string;
  /** P, exactly as entered. */
  originalUnitPrice: string;
  /** P x 0.82, the price printed on the invoice. */
  taxableUnitPrice: string;
  taxableAmount: string;
  gstAmount: string;
  lineTotal: string;
  position: number;
}

export interface GstBankDetails {
  accountName: string;
  bankName: string;
  /** Masked unless the viewer may see account numbers. */
  accountNumber: string | null;
  currency: string;
}

export interface GstInvoiceSummary {
  id: string;
  number: string;
  subject: string;
  status: (typeof GST_INVOICE_STATUSES)[number];
  invoiceDate: string;
  dueDate: string | null;
  account: RecordRef | null;
  currency: string;
  baseCurrency: string;
  exchangeRate: string;
  baseTotal: string;
  /** Sum of the taxable amounts. */
  subtotal: string;
  gstAmount: string;
  total: string;
  amountPaid: string;
  balanceDue: string;
  businessManager: BusinessManagerRef | null;
  createdAt: string;
  updatedAt: string;
}

export interface GstPaymentView {
  id: string;
  amount: string;
  receivedOn: string;
  method: (typeof PAYMENT_METHODS)[number];
  reference: string | null;
  notes: string | null;
  bankAccountName: string | null;
  createdAt: string;
}

export interface GstInvoiceDetail extends GstInvoiceSummary {
  contact: RecordRef | null;
  notes: string | null;
  terms: string | null;
  lineItems: GstInvoiceLineView[];
  bank: GstBankDetails | null;
  payments: GstPaymentView[];
  issuedAt: string | null;
  voidedAt: string | null;
  voidReason: string | null;
}

export interface GstReportRow {
  id: string;
  number: string;
  status: (typeof GST_INVOICE_STATUSES)[number];
  invoiceDate: string;
  customer: string | null;
  currency: string;
  taxable: string;
  gst: string;
  total: string;
  paid: string;
  balance: string;
}

export interface GstReport {
  from: string | null;
  to: string | null;
  bank: GstBankDetails | null;
  /** One block per currency: GST never sums across currencies. */
  totals: {
    currency: string;
    invoices: number;
    taxable: string;
    gst: string;
    total: string;
    paid: string;
    outstanding: string;
  }[];
  rows: GstReportRow[];
}
